import { Component, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../core/auth.service';
import { errorMessage } from '../core/http-error';
import { LanguageService } from '../core/language';
import { PHONE_COUNTRIES, PhoneCountry, flag, toE164 } from './phone-countries';

function fold(value: string): string {
  return value.trim().toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
}

function searchKeys(item: PhoneCountry): string[] {
  const name = fold(item.name);
  const keys = [name, item.dial, `+${item.dial}`, item.iso.toLowerCase()];
  if (name.includes('turkiye')) {
    keys.push(name.replaceAll('turkiye', 'turkey'));
  }
  return keys;
}

function passwordsMatch(control: AbstractControl): ValidationErrors | null {
  const password = control.get('password')?.value;
  const confirm = control.get('confirm')?.value;
  return password === confirm ? null : { mismatch: true };
}

@Component({
  selector: 'app-signup',
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './signup.html',
})
export class SignUpPage {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  protected readonly lang = inject(LanguageService);
  protected readonly submitting = signal(false);
  protected readonly serverError = signal<string | null>(null);
  protected readonly codeOpen = signal(false);
  protected readonly codeQuery = signal('');
  protected readonly countries = PHONE_COUNTRIES;
  protected readonly flag = flag;

  private readonly reachablePhone = (control: AbstractControl): ValidationErrors | null => {
    const iso = control.parent?.get('dial')?.value;
    const country = this.countries.find((item) => item.iso === iso);
    if (!country) {
      return { phone: true };
    }
    return toE164(String(control.value ?? ''), country) ? null : { phone: true };
  };

  protected readonly form = inject(FormBuilder).nonNullable.group(
    {
      name: ['', [Validators.required, Validators.maxLength(80)]],
      email: ['', [Validators.required, Validators.email]],
      dial: ['', Validators.required],
      national: ['', [Validators.required, this.reachablePhone]],
      country: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirm: ['', Validators.required],
    },
    { validators: passwordsMatch },
  );

  constructor() {
    if (this.auth.signedIn()) {
      void this.router.navigateByUrl(this.auth.user()?.detailsPending ? '/details' : '/portfolio');
    }
    this.form.controls.country.valueChanges.subscribe((iso) => {
      if (iso && this.form.controls.dial.value !== iso) {
        this.form.controls.dial.setValue(iso);
      }
    });
    this.form.controls.dial.valueChanges.subscribe((iso) => {
      if (iso && this.form.controls.country.value !== iso) {
        this.form.controls.country.setValue(iso);
      }
      this.form.controls.national.updateValueAndValidity();
    });
  }

  protected dialCountry(): PhoneCountry | undefined {
    return this.countries.find((item) => item.iso === this.form.controls.dial.value);
  }

  protected codeMatches(): PhoneCountry[] {
    const query = fold(this.codeQuery());
    const digits = query.replace(/^\+/, '');
    if (!query) {
      return this.countries;
    }
    return this.countries.filter((item) => searchKeys(item).some((key) => key.includes(query) || key.startsWith(digits)));
  }

  protected toggleCode(): void {
    const open = !this.codeOpen();
    this.codeOpen.set(open);
    if (!open) {
      return;
    }
    this.codeQuery.set('');
    queueMicrotask(() => document.getElementById('phone-code-search')?.focus());
  }

  protected setCodeQuery(event: Event): void {
    this.codeQuery.set((event.target as HTMLInputElement).value);
  }

  protected pickCode(item: PhoneCountry): void {
    this.form.controls.dial.setValue(item.iso);
    this.form.controls.dial.markAsTouched();
    this.codeQuery.set('');
    this.codeOpen.set(false);
  }

  protected closeCode(event: FocusEvent): void {
    const next = event.relatedTarget;
    const host = event.currentTarget;
    if (next instanceof Node && host instanceof HTMLElement && host.contains(next)) {
      return;
    }
    this.codeOpen.set(false);
  }

  protected submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting.set(true);
    this.serverError.set(null);
    const { name, email, national, country, password } = this.form.getRawValue();
    const dial = this.dialCountry();
    const phone = dial ? toE164(national, dial) : null;
    if (!phone) {
      this.form.controls.national.markAsTouched();
      this.submitting.set(false);
      return;
    }
    this.auth.register(name, email, phone, country, password).subscribe({
      next: () => void this.router.navigateByUrl('/details'),
      error: (error: unknown) => {
        this.submitting.set(false);
        this.serverError.set(errorMessage(error, this.lang.t('upFail')));
      },
    });
  }
}
